import { Loader, Mail } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useSendTestNotification } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

/**
 * Sends one test approval email to the instance's configured recipients, so an
 * admin can check the whole chain without waiting for a real approval. It
 * counts against the hourly email budget; the server answers 503 while
 * approval emails are off on the instance.
 */
export function SendTestEmailButton() {
  const send = useSendTestNotification();

  const handleSend = async () => {
    try {
      const result = await send.mutateAsync();
      logger.debug("[it-approvals] test email:", result?.status, result?.error_code);
      if (result?.status === "accepted") {
        toast.success(
          `Test email accepted for ${result.recipients} recipient(s) (operation ${result.operation_id ?? "unknown"}).`,
        );
      } else if (result?.status === "throttled") {
        toast.warning("This hour's email budget is spent. Nothing was sent; try again later.");
      } else {
        toast.error(`The test email was not sent (${result?.error_code ?? "unknown error"}).`);
      }
    } catch (err) {
      logger.error("[it-approvals] test email failed:", err);
      toast.error(readApiErrorMessage(err, "Couldn't send the test email. Please try again."));
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={() => void handleSend()} disabled={send.isPending}>
      {send.isPending ? <Loader className="animate-spin" /> : <Mail />}
      {send.isPending ? "Sending…" : "Send test email"}
    </Button>
  );
}
